import { useEffect, useState } from 'react';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';

import { isIOSApp } from '#features/api/utils/appCheck';
import { envConfig } from '#constants/envConfig';

type ReCaptchaProviderProps = {
  children: React.ReactNode;
};

/**
 * ⚠️ Библиотека под нами пропатчена — `patches/react-google-recaptcha-v3@1.11.0.patch`. Её
 * teardown писал `window.___grecaptcha_cfg = undefined`, а в этом объекте живёт очередь колбэков
 * `grecaptcha.ready()`; провайдер отдаёт `executeRecaptcha` только внутри такого колбэка, поэтому
 * стёртая очередь оставляла человека без токена до перезагрузки страницы. Ловилось не при
 * «блокировщике рекламы», а при обычном уходе со страницы до загрузки async-скрипта. Патч убирает
 * ровно это присваивание; сторож — `__tests__/recaptchaLibraryPatch.test.ts`.
 */

/**
 * Сколько ждём загрузки скрипта, прежде чем уйти на зеркало. Скрипт грузится с `async defer`,
 * так что на медленной мобильной сети несколько секунд — норма. Порог заведомо щедрый: цена
 * ложного срабатывания выше цены ожидания — переинжект сносит уже загруженный скрипт.
 */
const SCRIPT_LOAD_TIMEOUT_MS = 5000;

/**
 * Признак — наличие самого `grecaptcha`, а НЕ его `execute`. `api.js` определяет глобал сразу, а
 * `execute` появляется позже, когда догрузится языковой бандл и отработает `grecaptcha.ready()`.
 * Проверка по `execute` принимала «ещё инициализируется» за «не загрузился» и сносила рабочий
 * скрипт на полпути — от такого сноса Google кидает «Missing required parameters: sitekey».
 * Зеркало нужно ровно в одном случае: до хоста вообще не достучались, и глобала нет.
 */
const isRecaptchaLoaded = () =>
  typeof window !== 'undefined' && (window as { grecaptcha?: unknown }).grecaptcha !== undefined;

export const ReCaptchaProvider = ({ children }: ReCaptchaProviderProps) => {
  /**
   * `recaptcha.net` — тот же сервис Google на втором хосте (тот же site-key, тот же siteverify),
   * заведённый как раз для мест, где `google.com` не открывается. Хост не выбирается по региону
   * и никуда не зашит: грузим с основного, и только если скрипт так и не появился — переходим на
   * зеркало. Смена пропа роняет старый тег и инжектит новый (провайдер держит его в зависимостях
   * эффекта), так что дотянувшемуся хоть до одного из хостов капча достанется.
   */
  const [useMirror, setUseMirror] = useState(false);
  const nativeIOS = isIOSApp();

  useEffect(() => {
    if (nativeIOS || !envConfig.RECAPTCHA_KEY || useMirror) return;

    const timer = setTimeout(() => {
      if (!isRecaptchaLoaded()) {
        setUseMirror(true);
      }
    }, SCRIPT_LOAD_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [nativeIOS, useMirror]);

  if (nativeIOS || !envConfig.RECAPTCHA_KEY) {
    return <>{children}</>;
  }

  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={envConfig.RECAPTCHA_KEY}
      useRecaptchaNet={useMirror}
      scriptProps={{
        async: true,
        defer: true,
      }}
    >
      {children}
    </GoogleReCaptchaProvider>
  );
};
